<template>
    <div class="signin-page">
        <!-- 导航栏 -->
        <div class="nav-header">
            <button class="back-btn" @click="goBack">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </button>
        </div>

        <!-- 主要内容 -->
        <div class="main-content mt-30px">
            <!-- 标题区域 -->
            <div class="title-section">
                <div class="main-title">{{ t('login.title') }}</div>
                <p class="subtitle">{{ t('login.subtitle') }}</p>
            </div>

            <!-- 表单区域 -->
            <div class="form-section">
                <!-- 邮箱输入 -->
                <div class="input-group">
                    <label class="input-label" for="email">{{ t('login.email') }}</label>
                    <div class="input-wrapper" @touchstart.prevent.stop="handleFieldTouchStart('email')">
                        <input id="email" ref="emailInputRef" v-model="formData.email" type="email" class="form-input"
                            :placeholder="t('login.email')" autocomplete="email" @focus="handleInputFocus"
                            @blur="handleInputBlur" />
                    </div>
                </div>

                <!-- 密码输入 -->
                <div class="input-group">
                    <label class="input-label" for="password">{{ t('login.password') }}</label>
                    <div class="input-wrapper" @touchstart.prevent.stop="handleFieldTouchStart('password')">
                        <input id="password" ref="passwordInputRef" v-model="formData.password"
                            :type="showPassword ? 'text' : 'password'" class="form-input"
                            :placeholder="t('login.password')" autocomplete="current-password" @focus="handleInputFocus"
                            @blur="handleInputBlur" />
                        <button type="button" class="password-toggle" @click="togglePasswordVisibility"
                            @touchstart.stop>
                            <svg v-if="!showPassword" width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor"
                                    stroke-width="2" />
                                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
                            </svg>
                            <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M17.94 17.94A10.07 10.07 0 0 1 12 20C5 20 1 12 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94Z"
                                    stroke="currentColor" stroke-width="2" />
                                <path
                                    d="M9.9 4.24A9.12 9.12 0 0 1 12 4C19 4 23 12 23 12A18.5 18.5 0 0 1 19.24 15.1L9.9 4.24Z"
                                    stroke="currentColor" stroke-width="2" />
                                <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" />
                            </svg>
                        </button>
                    </div>
                </div>

                <!-- 注册链接 -->
                <!-- <div class="register-link">
                    <span class="register-text">{{ t('login.dontHaveAccount') }}</span>
                    <button type="button" class="register-btn" @click="goToRegister">{{ t('login.register') }}</button>
                </div> -->

                <!-- 登录按钮 -->
                <div class="button-section">
                    <button @click="handleSignIn" class="signin-btn" :class="{ 'btn-enabled': isFormValid }">
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { showErrorToast, showLoadingToast, closeToast, showSuccessToast } from '@/utils/toast'
import { ref, computed } from 'vue'
import { theme } from '@/styles/theme'
import { useI18n } from 'vue-i18n'
import verification from '@/utils/verification'
import { useUserStore } from '@/stores/user'
import { useRouter } from 'vue-router'
import CryptoJS from 'crypto-js'
import { useWaterfallStore } from '@/stores/waterfall'
import { useMessageStore } from '@/stores/message'
import { useStorePageStore } from '@/stores/store'
import { useIOSKeyboard } from '@/composables/useIOSKeyboard'

const messageStore = useMessageStore()
const storePageStore = useStorePageStore()
const { verifyEmpty, verifyEmail } = verification()
const { t } = useI18n()

const userStore = useUserStore()
const waterfallStore = useWaterfallStore()

const router = useRouter()

// 简单判断是否为 iOS 设备（内嵌 WebView 也适用）
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent)

// 定义事件
const emit = defineEmits<{
    back: []
    register: []
}>()

// 表单数据
const formData = ref({
    email: '',
    password: ''
})

// 密码显示状态
const showPassword = ref(false)

// 表单是否有效
const isFormValid = computed(() => {
    return formData.value.email.trim() !== '' && formData.value.password.trim() !== ''
})

// 输入框引用，用于手动控制焦点
const emailInputRef = ref<HTMLInputElement | null>(null)
const passwordInputRef = ref<HTMLInputElement | null>(null)

// iOS 键盘处理
const { handleInputFocus, handleInputBlur } = useIOSKeyboard()

/**
 * 处理输入框点击（触摸）：
 * - 在 iOS 上：如果当前已经有另一个输入框聚焦，则先让其失焦并收起键盘，
 *   不立即聚焦新的输入框（需要用户再点一次），避免“切换时页面跳动”；
 * - 其他平台：直接聚焦目标输入框，行为与原生一致。
 */
const handleFieldTouchStart = (field: 'email' | 'password') => {
    const targetInput = field === 'email' ? emailInputRef.value : passwordInputRef.value
    if (!targetInput) return

    const activeElement = document.activeElement as HTMLElement | null

    if (
        isIOS &&
        activeElement &&
        activeElement !== targetInput &&
        activeElement.tagName === 'INPUT'
    ) {
        // iOS 上：从一个输入切换到另一个输入时，先让当前输入失焦并收起键盘
        activeElement.blur()
        // 不立刻 focus 新的输入框，避免在键盘未完全收起时再次触发滚动
        return
    }

    // 首次点击 / 当前无输入聚焦 / 非 iOS：正常聚焦目标输入框
    targetInput.focus()
}

// 返回
const goBack = () => {
    emit('back')
}

// 切换密码显示
const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value
}

// 跳转注册
const goToRegister = () => {
    emit('register')
}

// 登录
const handleSignIn = async () => {
    if (verifyEmpty(formData.value.password.trim()) || verifyEmpty(formData.value.email.trim().trim())) {
        showErrorToast(t('login.pleaseInputPasswordAndEmail'))
        return
    }
    if (!verifyEmail(formData.value.email.trim())) {
        showErrorToast(t('login.pleaseInputCorrectEmail'))
        return
    }

    try {
        showLoadingToast(t('login.loggingIn'))
        // 加密密码
        const encryptedPassword = CryptoJS.MD5(formData.value.password).toString();
        await userStore.register({ ...formData.value, password: encryptedPassword })

        // 刷新首页数据
        await Promise.allSettled([
            userStore.setLoginTouristFunc(),
            waterfallStore.getTabNavs(),
            waterfallStore.refresh(0, waterfallStore.tabNavs[0].id, true),
        ])
        router.push('/')
        showSuccessToast(t('login.loginSuccess'))

        Promise.allSettled([
            messageStore.switchTab(1),
            messageStore.switchTab(2),
            storePageStore.fetchRechargeItems(),
            storePageStore.fetchConsumptionRecords()
        ])
    } catch (error) {
        showErrorToast(error as string)
    }
}
</script>

<style lang="scss" scoped>
.signin-page {
    /* 使用与登录页相同的固定高度，减轻 iOS 键盘导致的高度变化跳动 */
    height: var(--app-height);
    min-height: var(--app-height);
    background: v-bind('theme.colors.background');
    display: flex;
    flex-direction: column;
    position: relative;
    /* 确保页面可以正常滚动 */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
}

// 状态栏
.status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    height: 44px;

    .time {
        color: #ffffff;
        font-size: 17px;
        font-weight: 600;
    }

    .status-icons {
        display: flex;
        align-items: center;
        gap: 4px;

        .signal-icon,
        .wifi-icon,
        .battery-icon {
            width: 18px;
            height: 12px;
            background: #ffffff;
            border-radius: 2px;
        }
    }
}

// 导航栏
.nav-header {
    padding: 0 9px;
    height: 44px;
    display: flex;
    align-items: center;

    .back-btn {
        background: none;
        border: none;
        color: #ffffff;
        padding: 8px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            opacity: 0.7;
        }
    }
}

// 主要内容
.main-content {
    // flex: 1;
    padding: 22px 20px 0px 20px;
}

// 标题区域
.title-section {
    margin-bottom: 30px;

    .main-title {
        font-weight: 900;
        font-size: 16px;
        color: #FFFFFF;
        margin: 0 0 9px 0;
        line-height: 1.2;
    }

    .subtitle {
        font-weight: 500;
        font-size: 12px;
        color: v-bind('theme.colors.textPrimary');
        margin: 0;
        line-height: 1.4;
    }
}

// 表单区域
.form-section {
    .input-group {
        margin-bottom: 24px;

        .input-label {
            display: block;
            color: #ffffff;
            font-size: 12px;
            font-weight: 500;
            margin-bottom: 8px;
        }

        .input-wrapper {
            position: relative;

            .form-input {
                width: 100%;
                background: v-bind('theme.colors.backgroundSecondary');
                border: none;
                border-radius: 10px;
                padding: 12px 10px;
                color: v-bind('theme.colors.textPrimary');
                /* iOS 优化：防止自动缩放 */
                font-size: 16px;
                outline: none;
                transition: all 0.2s ease;
                -webkit-text-size-adjust: 100%;

                &::placeholder {
                    color: v-bind('theme.colors.textSecondary');
                }

                &:focus {
                    background: rgba(255, 255, 255, 0.15);
                }
            }

            .password-toggle {
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                background: none;
                border: none;
                color: v-bind('theme.colors.textSecondary');
                cursor: pointer;
                padding: 4px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                    color: #ffffff;
                }
            }
        }
    }
}

// 注册链接
.register-link {
    text-align: center;
    margin-top: 27px;

    .register-text {
        color: v-bind('theme.colors.textSecondary');
        font-weight: 500;
        font-size: 10px;
    }

    .register-btn {
        background: none;
        border: none;
        color: #ABB6FF;
        text-decoration: underline;
        cursor: pointer;
        margin-left: 4px;
        font-weight: 900;
        font-size: 10px;

        &:hover {
            opacity: 0.8;
        }
    }
}

// 按钮区域
.button-section {
    padding: 10px 20px 0px 20px;

    .signin-btn {
        width: 100%;
        height: 50px;
        background: linear-gradient(#5A68FF, #5A68FF 0%, #95A5FF 100%);
        border: none;
        border-radius: 23px;
        color: #ffffff;
        font-size: 16px;
        font-weight: 600;
        transition: all 0.2s ease;
        font-weight: 900;
        font-size: 14px;
        color: #FFFFFF;
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
        /* 禁止点击 */
    }

    .btn-enabled {
        opacity: 1 !important;
        cursor: pointer !important;
        pointer-events: auto !important;
        /* 允许点击 */
    }
}
</style>